import React, {
  useState,
  ChangeEvent,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AREA_CODES } from "../../assests/Areacodes";
import { UsetempUser } from "../../context/TempuserContext";
import { verifyProfileComplete } from "../../services/apis";

const DEFAULT_COUNTRY_CODE = "+1";

export interface YourinformationRef {
  validate: () => boolean;
}

const formatPhone = (raw: string, code: string): string => {
  const cleaned = raw.replace(/\D/g, "");

  if (code === "+1") {
    const limited = cleaned.slice(0, 10);

    if (limited.length <= 3) return limited;
    if (limited.length <= 6)
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6, 10)}`;
  }

  return cleaned;
};

const validatePhone = (code: string, number: string): string => {
  const cleaned = number.replace(/\D/g, "");

  if (code === "+1") {
    if (cleaned.length < 10) return "Phone number must be 10 digits";
    if (cleaned.length > 10) return "Phone number must be exactly 10 digits";
    const area = parseInt(cleaned.slice(0, 3));
    console.log("Validating area code:", area);
    if (!AREA_CODES.US.includes(area) && !AREA_CODES.CA.includes(area)) {
      return "Invalid phone number. Please enter a valid number.";
    }
    return "";
  }

  return "";
};

interface FormData {
  name: string;
  email: string;
  phonenumber: string;
  countryCode: string;
  promotionalEmails: boolean;
  promotionalTexts: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  phonenumber?: string;
  password?: string;
  otp?: string;
  confirmPassword?: string;
}

interface FormTouched {
  name?: boolean;
  email?: boolean;
  phonenumber?: boolean;
}

const Yourinformation = forwardRef<YourinformationRef>((_, ref) => {
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});
  const [authError, setAuthError] = useState<string>("");
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const { settempUser } = UsetempUser();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phonenumber: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    promotionalEmails: true,
    promotionalTexts: true,
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phonenumber) {
      newErrors.phonenumber = "Phone is required";
    } else {
      const phoneError = validatePhone(
        formData.countryCode,
        formData.phonenumber,
      );
      if (phoneError) {
        newErrors.phonenumber = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate: validateForm,
  }));

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { name, value } = e.target;
    const nextErrors: FormErrors = {};

    if (name === "name") {
      if (!value.trim()) nextErrors.name = "Name is required";
    }

    if (name === "phonenumber") {
      if (!formData.phonenumber) nextErrors.phonenumber = "Phone is required";
      else {
        const phoneError = validatePhone(
          formData.countryCode,
          formData.phonenumber,
        );
        if (phoneError) nextErrors.phonenumber = phoneError;
      }
    }

    if (name === "email") {
      if (!value.trim()) nextErrors.email = "Email is required";
      else if (!/^\S+@\S+\.\S+$/.test(value)) {
        nextErrors.email = "Please enter a valid email";
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
    }
  };

  const handleChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): Promise<void> => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData };
    let updatedTouched: FormTouched = { ...touched, [name]: true };
    let resolvedUserId: string | undefined;

    if (name === "phonenumber") {
      const formatted = formatPhone(value, formData.countryCode);
      updatedFormData.phonenumber = formatted;

      const cleanedPhone = formatted.replace(/\D/g, "");
      if (formData.countryCode === "+1" && cleanedPhone.length === 10) {
        const phoneError = validatePhone(formData.countryCode, formatted);
        if (!phoneError) {
          try {
            const response = await verifyProfileComplete(cleanedPhone);
            console.log(
              "Profile is complete, pre-filling information",
              response,
            );
            if (response.data.isProfileComplete) {
              console.log(
                "Profile is complete, pre-filling information",
                response,
              );
              resolvedUserId =
                response.data?.user?.id || response.data?.user?._id;
              updatedFormData.email = response.data.user.email;
              updatedFormData.name = response.data.user.name;
              updatedFormData.promotionalEmails =
                response.data.user.promotionalEmails;
              updatedFormData.promotionalTexts =
                response.data.user.promotionalTexts;
              updatedTouched.email = false;
              updatedTouched.name = false;
            } else {
              updatedFormData.email = "";
              updatedFormData.name = "";
              updatedTouched.email = false;
              updatedTouched.name = false;
            }
          } catch (error) {
            console.error("Error verifying profile:", error);
          }
        }
      }
    } else {
      updatedFormData = { ...updatedFormData, [name]: value };
    }
    setFormData(updatedFormData);
    setTouched(updatedTouched);

    // Update tempUser immediately on every change
    const cleanedPhone = updatedFormData.phonenumber.replace(/\D/g, "");
    settempUser((prevUser: any) => ({
      ...prevUser,
      id:
        name === "phonenumber"
          ? resolvedUserId || undefined
          : prevUser?.id,
      name:
        typeof updatedFormData?.name === "string"
          ? updatedFormData.name.trim()
          : "",
      email:
        typeof updatedFormData?.email === "string"
          ? updatedFormData.email.trim()
          : "",
      phonenumber: !errors.phonenumber ? cleanedPhone : "",
      promotionalEmails: Boolean(updatedFormData?.promotionalEmails),
      promotionalTexts: Boolean(updatedFormData?.promotionalTexts),
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (authError) {
      setAuthError("");
    }
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };
  const isFormValid = (): boolean => {
    const name = typeof formData?.name === "string" ? formData.name.trim() : "";

    const email =
      typeof formData?.email === "string" ? formData.email.trim() : "";

    const phone =
      typeof formData?.phonenumber === "string"
        ? formData.phonenumber.trim()
        : "";

    if (!name) return false;
    if (!email) return false;

    if (!/^\S+@\S+\.\S+$/.test(email)) return false;

    if (!phone) return false;

    const phoneError = validatePhone(formData?.countryCode ?? "", phone);

    if (phoneError) return false;

    return true;
  };

  useEffect(() => {
    if (isFormValid()) {
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }
  }, [formData]);

  return (
    <div className="bg-white rounded-lg border-[1px] border-black/10 p-4 mb-4 dm-info-form dm-auth-modal">
      <p className="font-semibold text-[17px] sm:text-[19px] text-black mb-4">
        Your Information
      </p>

      <div className="my-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <div className="flex gap-2">
          <div className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 flex items-center">
            {DEFAULT_COUNTRY_CODE}
          </div>
          <input
            type="tel"
            name="phonenumber"
            value={formData.phonenumber}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="123-456-7890"
            className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              errors.phonenumber
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-black"
            } ${
              !formData.phonenumber
                ? "dm-auth-empty"
                : touched.phonenumber
                  ? "dm-auth-filled"
                  : "dm-auth-prefilled"
            }`}
          />
        </div>
        {errors.phonenumber && (
          <p className="text-red-500 text-xs mt-1">{errors.phonenumber}</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="John Doe"
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
            errors.name
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-black"
          } ${!formData.name ? "dm-auth-empty" : "dm-auth-filled"}`}
        />
        {errors.name && (
          <p className="text-red-500 text-xs mt-1">{errors.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="john@example.com"
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
            errors.email
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-black"
          } ${!formData.email ? "dm-auth-empty" : "dm-auth-filled"}`}
        />
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      <div className="flex flex-col mt-3 gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.promotionalEmails}
            onChange={(e) => {
              const checked = e.target.checked;
              setFormData((prev) => ({
                ...prev,
                promotionalEmails: checked,
              }));
              settempUser((prevUser: any) => ({
                ...prevUser,
                promotionalEmails: checked,
              }));
            }}
            className="dm-info-checkbox w-6 h-6 cursor-pointer appearance-none border border-gray-400 rounded-md 
                      checked:bg-black checked:border-black
                      checked:after:content-['✓'] checked:after:text-white 
                      checked:after:flex checked:after:items-center 
                      checked:after:justify-center checked:after:text-sm
                      flex items-center justify-center"
          />
          <span className="text-sm">Receive promotional emails</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.promotionalTexts}
            onChange={(e) => {
              const checked = e.target.checked;
              setFormData((prev) => ({
                ...prev,
                promotionalTexts: checked,
              }));
              settempUser((prevUser: any) => ({
                ...prevUser,
                promotionalTexts: checked,
              }));
            }}
            className="w-6 h-6 cursor-pointer dm-info-checkbox appearance-none border border-gray-400 rounded-md 
                      checked:bg-black checked:border-black
                      checked:after:content-['✓'] checked:after:text-white 
                      checked:after:flex checked:after:items-center 
                      checked:after:justify-center checked:after:text-sm
                      flex items-center justify-center"
          />
          <span className="text-sm">Receive promotional text messages</span>
        </label>
      </div>

      {/* Success Message */}
      {/* {saveSuccess && (
        <p className="text-green-600 text-sm mt-2 text-center font-medium">
          Information saved successfully!
        </p>
      )} */}

      {/* Auth Error Message */}
      {authError && (
        <p className="text-red-500 text-sm mt-2 text-center">{authError}</p>
      )}
    </div>
  );
});

export default Yourinformation;
